import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { LoadingSkeleton } from '@/components/ui/loading-spinner';
import { 
  Users, 
  FileText, 
  Calendar,
  UserCheck,
  Clock
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

interface SimpleRecruitmentMetrics {
  totalJobPostings: number;
  activeJobPostings: number;
  totalApplications: number;
  pendingApplications: number;
  scheduledInterviews: number;
  totalHires: number;
  recentActivity: Array<{
    type: 'application' | 'interview' | 'hire';
    jobTitle: string;
    applicantName: string;
    date: string;
  }>;
}

interface SimpleRecruitmentAnalyticsProps {
  businessId: string;
  loading?: boolean;
}

const SimpleRecruitmentAnalytics: React.FC<SimpleRecruitmentAnalyticsProps> = ({
  businessId,
  loading: externalLoading = false
}) => {
  const [metrics, setMetrics] = useState<SimpleRecruitmentMetrics | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (businessId) {
      fetchSimpleMetrics();
    }
  }, [businessId]);

  const fetchSimpleMetrics = async () => {
    try {
      setLoading(true);

      // Fetch job postings
      const { data: jobPostings } = await supabase
        .from('job_postings')
        .select('id, title, is_active')
        .eq('business_id', businessId);

      // Fetch applications
      const { data: applications } = await supabase
        .from('job_applications')
        .select(`
          id,
          status,
          applicant_name,
          submitted_at,
          job_postings!inner(title)
        `)
        .eq('business_id', businessId)
        .order('submitted_at', { ascending: false });

      // Fetch recent interviews
      const { data: interviews } = await supabase
        .from('interview_schedules')
        .select(`
          id,
          scheduled_at,
          job_applications!inner(
            applicant_name,
            job_postings!inner(title)
          )
        `)
        .eq('job_applications.business_id', businessId)
        .gte('scheduled_at', new Date().toISOString())
        .order('scheduled_at', { ascending: true });

      // Calculate simple metrics
      const totalJobPostings = jobPostings?.length || 0;
      const activeJobPostings = jobPostings?.filter(job => job.is_active).length || 0;
      const totalApplications = applications?.length || 0;
      const pendingApplications = applications?.filter(app => 
        ['submitted', 'screening', 'interview-scheduled'].includes(app.status)
      ).length || 0;
      const scheduledInterviews = interviews?.length || 0;
      const totalHires = applications?.filter(app => app.status === 'hired').length || 0;

      // Create recent activity (last 10 items)
      const recentActivity: SimpleRecruitmentMetrics['recentActivity'] = [];
      
      // Add recent applications
      applications?.slice(0, 5).forEach(app => {
        recentActivity.push({
          type: app.status === 'hired' ? 'hire' : 'application',
          jobTitle: app.job_postings?.title || 'Unknown Job',
          applicantName: app.applicant_name || 'Anonymous',
          date: app.submitted_at
        });
      });

      // Add upcoming interviews
      interviews?.slice(0, 3).forEach(interview => {
        recentActivity.push({
          type: 'interview',
          jobTitle: interview.job_applications?.job_postings?.title || 'Unknown Job',
          applicantName: interview.job_applications?.applicant_name || 'Anonymous',
          date: interview.scheduled_at
        });
      });

      // Sort by date and take most recent 10
      recentActivity.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

      setMetrics({
        totalJobPostings,
        activeJobPostings,
        totalApplications,
        pendingApplications,
        scheduledInterviews,
        totalHires,
        recentActivity: recentActivity.slice(0, 10)
      });

    } catch (error) {
      console.error('Error fetching simple recruitment metrics:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading || externalLoading) {
    return <LoadingSkeleton variant="analytics" />;
  }

  if (!metrics) {
    return null;
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    return date.toLocaleDateString();
  };

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'application': return <Users className="h-3 w-3" />;
      case 'interview': return <Calendar className="h-3 w-3" />;
      case 'hire': return <UserCheck className="h-3 w-3" />;
      default: return <FileText className="h-3 w-3" />;
    }
  };

  const getActivityColor = (type: string) => {
    switch (type) {
      case 'application': return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400';
      case 'interview': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400';
      case 'hire': return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';
    }
  };

  return (
    <div className="space-y-4">
      {/* Key Metrics Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Job Postings</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{metrics.totalJobPostings}</div>
            <p className="text-xs text-muted-foreground">
              {metrics.activeJobPostings} active
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Applications</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{metrics.totalApplications}</div>
            <p className="text-xs text-muted-foreground">
              {metrics.pendingApplications} pending
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Interviews</CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{metrics.scheduledInterviews}</div>
            <p className="text-xs text-muted-foreground">
              upcoming
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Hires</CardTitle>
            <UserCheck className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{metrics.totalHires}</div>
            <p className="text-xs text-muted-foreground">
              total hired
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Recent Activity */}
      {metrics.recentActivity.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg font-medium flex items-center gap-2">
              <Clock className="h-5 w-5" />
              Recent Activity
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {metrics.recentActivity.map((activity, index) => (
                <div key={index} className="flex items-center justify-between py-2 border-b last:border-b-0">
                  <div className="flex items-center gap-3">
                    <Badge className={`${getActivityColor(activity.type)} flex items-center gap-1`}>
                      {getActivityIcon(activity.type)}
                      {activity.type}
                    </Badge>
                    <div>
                      <p className="text-sm font-medium">{activity.applicantName}</p>
                      <p className="text-xs text-muted-foreground">{activity.jobTitle}</p>
                    </div>
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {formatDate(activity.date)}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default SimpleRecruitmentAnalytics;